// src/oura/components/onboarding/BusinessRecordCard.tsx
//
// "Business record": whether the name, the website and each outlet's address
// and phone agree between the signed quote and the Google profile (#4153,
// Production Readiness item 1). Staff only, read-only.
//
// The backend compares and says so (`businessRecordCheck`, ourteam-backend
// #5808); this draws what it sent. It draws NOTHING while the check is absent:
// a business with no engagement, a backend that does not send it yet, a read
// that failed. Absent is unknown, never a column of "Not recorded".

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, ClipboardCheckIcon } from "lucide-react";
import { FOCUS_RING, FoldCard, Tag, TYPE, type Tone } from "@oura/ui";
import { getEngagementIfExists, type BusinessRecordCheck, type OutletName, type RecordCheckState } from "@service/aeo/clientReview";
import { ouraHref } from "../../nav";
import { cn } from "../../lib/cn";
import { RECORD_STATE_WORD, googleProfileHash, recordRows, recordSummary, type RecordRow } from "../../onboarding/recordCheck";

type State =
    | { kind: "loading" }
    | { kind: "none" }
    /** `businessId` is the business the check was read for: a result is only ever drawn for that business. */
    | { kind: "ready"; businessId: string; check: BusinessRecordCheck; outletNames: OutletName[] | null };

const STATE_TONE: Record<RecordCheckState, Tone> = { match: "green", mismatch: "amber", missing: "gray" };

export function BusinessRecordCard({ businessId }: { businessId: string }) {
    const [state, setState] = useState<State>({ kind: "loading" });

    useEffect(() => {
        let cancelled = false;
        setState({ kind: "loading" });
        getEngagementIfExists(businessId)
            .then((e) => {
                if (cancelled) return;
                setState(e?.businessRecordCheck ? { kind: "ready", businessId, check: e.businessRecordCheck, outletNames: e.outletNames ?? null } : { kind: "none" });
            })
            .catch(() => { if (!cancelled) setState({ kind: "none" }); });
        return () => { cancelled = true; };
    }, [businessId]);

    // The effect resets the state AFTER the render that gets a new `businessId`,
    // so that one render still holds the previous business's result: refused here
    // by its own business id, never drawn under the new name (Jack, #4215).
    if (state.kind !== "ready" || state.businessId !== businessId) return null;
    return <BusinessRecordView businessId={businessId} check={state.check} outletNames={state.outletNames} />;
}

/**
 * The card for a check that was read: the part with something to draw, apart from the read.
 *
 * Short on purpose (Jack): every field is one line, its name and its state. The
 * two values show only where they differ, the one case where staff must read
 * them. A business with several outlets folds their addresses and phones into
 * one row that says how they stand; it opens by itself when one of them differs.
 */
export function BusinessRecordView({ businessId, check, outletNames }: { businessId: string; check: BusinessRecordCheck; outletNames: OutletName[] | null }) {
    const rows = recordRows(check, outletNames);
    const summary = recordSummary(rows);
    const reviews = ouraHref("reviews", { role: "oura-staff", businessId });
    const plan = `${ouraHref("delivery", { role: "oura-staff", businessId })}?view=plan&section=outlet-names`;
    const several = check.outlets.length > 1;
    const own = several ? rows.filter((r) => r.outlet === null) : rows;
    const outletRows = several ? rows.filter((r) => r.outlet !== null) : [];

    return (
        <FoldCard id="business-record" title="Business record" icon={{ icon: ClipboardCheckIcon, area: "setup" }} foldedFact={summary}>
            <p className={cn(TYPE.tc, "m-0 pb-2 text-gray-500")}>
                {summary}. The signed quote against the Google profile.
            </p>
            <ul className="list-plain m-0 divide-y divide-gray-100 p-0">
                {own.map((row) => <FieldRow key={row.key} row={row} reviews={reviews} plan={plan} />)}
                {outletRows.length > 0 && <OutletsRow count={check.outlets.length} rows={outletRows} reviews={reviews} plan={plan} />}
            </ul>
        </FoldCard>
    );
}

/** One field: its name and its state on one line, and both values only when they differ. */
function FieldRow({ row, reviews, plan, nested }: { row: RecordRow; reviews: string; plan?: string; nested?: boolean }) {
    const { state, quote, google } = row.field;
    // A side not recorded: the state links to the form that records it. Google's
    // first; then the quote's address and phone, entered under the outlet names
    // in Plan (#4776). The name and website on the quote are not typed there.
    const quoteField = /(address|phone)$/.test(row.key);
    const to =
        state !== "missing"
            ? null
            : !google
                ? `${reviews}${googleProfileHash(row.outlet)}`
                : !quote && quoteField && plan
                    ? plan
                    : null;
    const where = !google ? "on the Google profile" : "under the outlet names in Plan";
    const tag = <Tag tone={STATE_TONE[state]}>{RECORD_STATE_WORD[state]}</Tag>;
    return (
        <li className={cn("py-2.5", nested && "pl-4")}>
            <div className="flex items-center justify-between gap-3">
                <span className={cn(TYPE.tb, "min-w-0 text-gray-900")}>{row.label}</span>
                {to ? (
                    <Link to={to} className={cn("flex-shrink-0 rounded-full", FOCUS_RING)} aria-label={`${row.label}: not recorded. Record it ${where}`}>
                        {tag}
                    </Link>
                ) : (
                    <span className="flex-shrink-0">{tag}</span>
                )}
            </div>
            {state === "mismatch" && (
                <dl className={cn(TYPE.tc, "m-0 mt-1 grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-0.5")}>
                    <dt className="text-gray-500">Signed quote</dt>
                    <dd className="m-0 break-words text-gray-900">{quote ?? "Not recorded"}</dd>
                    <dt className="text-gray-500">Google profile</dt>
                    <dd className="m-0 break-words text-gray-900">{google ?? "Not recorded"}</dd>
                </dl>
            )}
        </li>
    );
}

/** Every outlet's address and phone as one row: "3 outlets", how they stand, and a fold. */
function OutletsRow({ count, rows, reviews, plan }: { count: number; rows: RecordRow[]; reviews: string; plan: string }) {
    const differ = rows.some((r) => r.field.state === "mismatch");
    const [open, setOpen] = useState(differ);
    const tally = (s: RecordCheckState) => rows.filter((r) => r.field.state === s).length;
    const worst: RecordCheckState = differ ? "mismatch" : tally("missing") > 0 ? "missing" : "match";
    const said = worst === "match"
        ? "All match"
        : [tally("mismatch") > 0 && `${tally("mismatch")} differ`, tally("missing") > 0 && `${tally("missing")} not recorded`].filter(Boolean).join(", ");

    return (
        <li className="py-2.5">
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className={cn("flex w-full items-center justify-between gap-3 rounded text-left", FOCUS_RING)}
            >
                <span className={cn(TYPE.tb, "flex min-w-0 items-center gap-1.5 text-gray-900")}>
                    <ChevronRightIcon aria-hidden className={cn("h-4 w-4 flex-shrink-0 text-gray-400 transition-transform", open && "rotate-90")} />
                    {count} outlets, address and phone
                </span>
                <span className="flex-shrink-0"><Tag tone={STATE_TONE[worst]}>{said}</Tag></span>
            </button>
            {open && (
                <ul className="list-plain m-0 mt-1 divide-y divide-gray-100 p-0">
                    {rows.map((row) => <FieldRow key={row.key} row={row} reviews={reviews} plan={plan} nested />)}
                </ul>
            )}
        </li>
    );
}
